import { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { generateAdvancedFilter } from "reducers/common/ColumnFilters";
import { noop } from "./common";
import { debouncedTooltipRebuild } from "utils/Tooltip";
import { isEqual } from "lodash";

/*
	React Data Grid doesn't provide a reliable method to reset scroll posiiton, so we
	have to reach into its internals and set scrollTop/scrollLeft manually.

	`DATA_GRID_SCROLL_TARGET` provides the identifying selector for React Data Grid's
	scrollable element, so you can call `.querySelector(DATA_GRID_SCROLL_TARGET)` on
	a parent element, and set scroll attributes on the returned element.
*/
export const DATA_GRID_SCROLL_TARGET = ".react-grid-Canvas";

/*
	When a data grid's contents change, we want to be able to reset the scroll on the grid,
	and we want to rebuild the tooltips in the rows when the user scrolls through them, so
	that they appear correctly when hovering over elements with a `data-tip` attribute.

	This hook will return a ref that should be set to a parent element of the data grid.
	The element should be a basic element (e.g. a <div>, rather than a React component),
	so that `ref.current.querySelector()` will be defined.

	`dependencies` should be an array of dependencies as you'd pass to `useEffect()`, for
	what changes should trigger a rescroll.  Generally this will be the items that are shown
	in the data grid.

	`scrollTarget` is the identifying selector for the node whose scroll position you want
	to change.  By default, it's the scrollable node in React Data Grid.

	Usage:
	```
		import { DataGrid } from "rhombus-react";
		function Grid({ items }){
			const dataGridRef = useGridScrollUtils([items]);

			return (
				<div ref={dataGridRef}>
					<DataGrid />
				</div>
			);
		}
	```
*/
export function useGridScrollUtils(dependencies, scrollTarget=DATA_GRID_SCROLL_TARGET){
	const dataGridRef = useRef(null);

	useEffect(() => {
		let dataGrid = dataGridRef.current;
		/*
			querySelector only returns the first match it finds, so this should hopefully
			limit any unintended side effects in case we have any nested grids.
		*/
		let scrollContainer = dataGrid?.querySelector(scrollTarget);
		if(scrollContainer){
			scrollContainer.scrollTop = 0;
			scrollContainer.scrollLeft = 0;

			// We have to add +1 to the scroll positions to account for horizontal scrolling of locked columns
			scrollContainer.scrollTop += 1;
			scrollContainer.scrollLeft += 1;

			/*
				DataGrid's `onScroll` prop doesn't appear to work, so we have to touch the DOM to
				add a scroll handler that rebuilds the tooltips in the rows.
			*/
			scrollContainer.addEventListener("scroll", debouncedTooltipRebuild);
		}
	}, dependencies);

	return dataGridRef;
}


/*
	# Collection Filtering

	These utilities are used with the functionality provided by `reducers/common/Filtering`
	and `reducers/common/ColumnFilters`.

	`useCollectionFiltering()` is a hook that allows you to trigger a fetch whenever the `filterInfo` value
	in the provided collection changes.  It returns the `filterInfo` and `columnFilter` state.

	## Example Usage:

		```
			function CreativeLibrariesScreen(props){
				const dispatch = useDispatch();

				const libraries = useSelector(state => state.libraries);
				const {
					filterInfo: { page, rpp, searchTerm, sort },
					columnFilters,
				} = useCollectionFiltering(
					libraries,
					Libraries.setAdvancedFilter,
					() => { dispatch(Libraries.loadLibraries()) }
				);

				const pagerProps = {
					initialPage: page,
					forcePage: page,
					pageSize: rpp,

					onPageChange: newPage => dispatch(Libraries.setPage(newPage)),
					onRowCountChange: newRPP => dispatch(Libraries.setRPP(newRPP)),
				};

				return (
					<div>
						<Toolbar
							searchTerm = {searchTerm}
							handleSearch = {newSearchTerm => dispatch(Libraries.setSearchTerm(newSearchTerm))}
						/>
						<FilterTagList
							Reducer = {Libraries}
							filters = {columnFilters}
						/>
						<Grid
							libraries = {libraries}
							sort = {sort}
						/>
						<Pager
							{...pagerProps}
						/>
					</div>
				);
			}
		```

		Where `Libraries.loadLibraries` is an action creator that fetches new data using the params in `filterInfo`.

	## Preconfig

		The Filtering and ColumnFilters packages provide ways to set defaults when added to a reducer,
		but sometimes you may want different views on the same collections, with their own configurations.

		To do this, you can specify a setup function as an optional argument to `useCollectionFiltering`

		## Example Usage:
			```
				function LibrariesDefaultSortedByName(props){
					const libraries = useSelector(state => state.libraries);
					const {
						filterInfo: { page, rpp, searchTerm, sort },
						columnFilters,
					} = useCollectionFiltering(
						libraries,
						Libraries.setAdvancedFilter,
						() => { dispatch(Libraries.loadLibraries()) },
						() => {
							// Set up sorting by name before the fetch effect ever fires
							dispatch(Libraries.setSort("name", "ASC"));
						}
					);
				}
			```

		Redux makes the timing of setup difficult to manage with effects, so it's recommended that you use
		this argument instead of trying to manage setup from the outside.


		## Postconfig

			You may want to perform cleanup when your column filtered component unmounts, and you can do that
			by providing a setdown function as an optional argument, after the setup function.

			Usually you'll want to run `clearColumnFilters` on your collection, so that things like the current
			page number or search filters don't persist when changing collection views.
*/

export function useValueEffect(effect, dependency, referenceDependencies = []){
	/*
		This acts like useEffect(), but only fires the effect when the dependency's value changes,
		instead of when its reference changes, and only takes one dependency.

		You may provide an array of additional dependencies that work like normal reference dependencies
		for an effect, as an optional third argument.
	*/
	const [state, setState] = useState(dependency);

	useEffect(() => {
		setState((old) => {
			if(isEqual(old, dependency)){
				/*
					If the values are equal, we return the old value, which keeps the state's reference stable,
					so the effect isn't triggered.
				*/
				return old;
			}
			// Otherwise, update the reference to the new value and trigger the effect.
			return dependency;
		});
	}, [dependency]);

	useEffect(effect, [state, ...referenceDependencies]);
	return state;
}

function promiseAction(fn){
	return (dispatch) => {
		return new Promise((resolve) => {
			resolve(fn());
		});
	};
}

export function useCollectionFiltering(collection, setAdvancedFilterAction, fetchEffect, setup = noop, setdown = noop){
	const dispatch = useDispatch();
	const [finishedSetup, setFinishedSetup] = useState(false);

	useEffect(() => {
		dispatch(promiseAction(setup)).then(() => {
			/*
				Without a promise, we'd get finishedSetup changed between dispatches, if there are multiple
				redux actions involved in the setup, which would result in duplicate fetches.
			*/
			setFinishedSetup(true);
		});

		return setdown;
	}, []);

	const columnFilters = collection.columnFilters.active;
	const { filterInfo } = collection;

	useValueEffect(() => {
		/*
			`finishedSetup` is provided as a dependency for the effect to ensure we fetch at least once.
			Without it, there are cases where `filterInfo` would be stable too early, and we'd never fire fetchEffect.
		*/
		if(finishedSetup){
			fetchEffect();
		}
	}, filterInfo, [finishedSetup]);

	const compiledFilters = generateAdvancedFilter(columnFilters);

	useEffect(() => {
		dispatch(setAdvancedFilterAction(compiledFilters));
	}, [compiledFilters]);

	return {
		filterInfo,
		columnFilters,
	};
}
